import UserGuide from '@/components/help/UserGuide';
import { Input, Textarea } from '@/components/ui/FormControls';
import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, CheckCircle2, Loader2, Trash2, X } from 'lucide-react';
import type { RecycleJob } from '@klothiq/domain';
import { recycleDeletionReasonSchema } from '@klothiq/validation';
import { useRecycleJob } from '@/hooks/useRecycleBin';
import { recycleErrorMessage } from '@/lib/recycleErrors';
import { recycleButton } from './RecycleRecordCard';

export default function RecycleCleanupDialog({
  open,
  job,
  running,
  error,
  onClose,
  onExecute,
  onReview,
  onCancel,
}: {
  open: boolean;
  job: RecycleJob | null;
  running: boolean;
  error: string;
  onClose: () => void;
  onExecute: (reason: string, confirmation: string) => void;
  onReview: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [page, setPage] = useState(0);
  const details = useRecycleJob(open && job ? job.job_id : null, page * 50);
  const expected = `DELETE ${job?.eligible ?? 0} ITEMS`;
  const validation = recycleDeletionReasonSchema.safeParse(reason);
  useEffect(() => {
    setReason(job?.reason ?? '');
    setConfirmation('');
    setPage(0);
  }, [job?.job_id, open]);
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" data-field-guide-scope />
        <Dialog.Content
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            document.getElementById('recycle-search')?.focus();
          }}
          className="obsidian-panel fixed left-1/2 top-1/2 z-50 flex max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl focus:outline-none"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border p-5">
            <div>
              <div className="flex items-start justify-between gap-3 pr-8">
                <Dialog.Title className="text-lg font-semibold">
                  {job?.confirmed ? 'Cleanup result' : 'Review permanent deletion'}
                </Dialog.Title>
                <UserGuide compact />
              </div>
              <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                {job?.confirmed
                  ? 'Completed deletions cannot be undone. Remaining items can be resumed.'
                  : 'Only eligible records in this frozen selection will be deleted. Their audit evidence will remain.'}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label="Close cleanup dialog"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <div className="space-y-4 overflow-y-auto p-5">
            {job && (
              <>
                <div className="grid grid-cols-3 gap-2 rounded-xl bg-muted/50 p-3 text-center text-sm">
                  {[
                    ['In scope', job.total],
                    ['Eligible', job.eligible],
                    ['Protected', job.total - job.eligible],
                  ].map(([label, count]) => (
                    <div key={label}>
                      <p className="text-lg font-semibold">{count}</p>
                      <p className="text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                {!job.confirmed && (
                  <div className="flex gap-2 rounded-lg border border-destructive/25 bg-destructive/5 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <p>
                      Sale deletion includes its already voided payments and cash entries. Products
                      used in history and independently linked payments remain protected.
                    </p>
                  </div>
                )}
                {!job.confirmed && job.eligible > 0 && (
                  <>
                    <p className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
                      This selection includes {job.impact.payments} payment records,{' '}
                      {job.impact.cash_entries} cash entries, {job.impact.line_items} sale line
                      items and {job.impact.variants} product variants.
                    </p>
                    <div>
                      <label htmlFor="cleanup-reason" className="mb-2 block text-sm font-medium">
                        Deletion reason
                      </label>
                      <Textarea
                        id="cleanup-reason"
                        maxLength={500}
                        rows={3}
                        value={reason}
                        disabled={running}
                        onChange={(event) => setReason(event.target.value)}
                        aria-describedby="cleanup-reason-help"
                        className="w-full rounded-lg border border-border bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        helpLabel="Deletion reason"
                      />
                      <p id="cleanup-reason-help" className="mt-1 text-xs text-muted-foreground">
                        3–500 characters. Recorded with the audit trail.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="cleanup-confirmation"
                        className="mb-2 block text-sm font-medium"
                      >
                        Type <span className="font-mono">{expected}</span> to confirm
                      </label>
                      <Input
                        id="cleanup-confirmation"
                        value={confirmation}
                        disabled={running}
                        onChange={(event) => setConfirmation(event.target.value)}
                        autoComplete="off"
                        className="min-h-11 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        helpLabel="Type to confirm"
                      />
                    </div>
                  </>
                )}
                {job.confirmed && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="rounded-lg border border-border p-3 text-sm"
                  >
                    <p className="flex items-center gap-2 font-medium">
                      {running ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                      {running
                        ? 'Processing cleanup…'
                        : job.pending
                          ? 'Cleanup paused'
                          : 'Cleanup complete'}
                    </p>
                    <p className="mt-2 text-muted-foreground">
                      {job.deleted} deleted · {job.skipped} retained · {job.failed} failed ·{' '}
                      {job.pending} pending
                    </p>
                    <progress
                      aria-label="Cleanup progress"
                      value={job.total - job.pending}
                      max={Math.max(1, job.total)}
                      className="mt-3 h-2 w-full accent-primary"
                    />
                  </div>
                )}
                {!job.eligible && (
                  <p className="text-sm text-muted-foreground">
                    No records in this selection are eligible for permanent deletion.
                  </p>
                )}
                {details.isFetching && (
                  <p role="status" className="text-xs text-muted-foreground">
                    Refreshing item results…
                  </p>
                )}
                {details.error && (
                  <p role="alert" className="text-sm text-destructive">
                    {recycleErrorMessage(details.error)}
                  </p>
                )}
                <ul
                  className="max-h-48 space-y-2 overflow-y-auto text-sm"
                  aria-label="Cleanup item results"
                >
                  {(details.data?.results ?? job.results).map((item) => (
                    <li key={item.id} className="rounded-lg border border-border p-3">
                      <p className="break-words font-medium">{item.label}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {item.message || item.block_message || 'Ready to delete'}
                      </p>
                    </li>
                  ))}
                </ul>
                {job.total > 50 && (
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <button
                      className={`${recycleButton} border-border`}
                      disabled={running || page === 0}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Results {page + 1} / {Math.ceil(job.total / 50)}
                    </span>
                    <button
                      className={`${recycleButton} border-border`}
                      disabled={running || (page + 1) * 50 >= job.total}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
            {error && (
              <p
                role="alert"
                className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              >
                {error}
              </p>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 border-t border-border bg-muted/20 p-5 sm:flex-row sm:justify-end">
            <Dialog.Close className={`${recycleButton} border-border bg-background`}>
              {running ? 'Pause after current batch' : 'Close'}
            </Dialog.Close>
            {job && job.pending > 0 && (
              <button
                className={`${recycleButton} border-destructive bg-destructive text-white hover:bg-destructive/90`}
                disabled={
                  running || (!job.confirmed && (!validation.success || confirmation !== expected))
                }
                onClick={() =>
                  onExecute(
                    job.confirmed ? (job.reason ?? '') : validation.success ? validation.data : '',
                    expected,
                  )
                }
              >
                {running ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {job.confirmed
                  ? 'Resume cleanup'
                  : `Permanently delete ${job.eligible} item${job.eligible === 1 ? '' : 's'}`}
              </button>
            )}
            {job?.confirmed && !job.pending && job.failed + job.skipped > 0 && (
              <Dialog.Close className={`${recycleButton} border-border`} onClick={onReview}>
                Review remaining items
              </Dialog.Close>
            )}
            {job?.confirmed && job.pending > 0 && !running && (
              <button className={`${recycleButton} border-border`} onClick={onCancel}>
                Cancel remaining cleanup
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
